import { index } from '@w/db'
import { enumValues, sqliteTable } from '@w/db/sqlite'
import { s } from '@w/schema'

const emailProviderSchema = s.object({
  type: s.literal('email'),
  id: s.string(),
  verified: s.boolean(),
})

const googleProviderSchema = s.object({
  type: s.literal('google'),
  id: s.string(),
  email: s.string(),
  name: s.string(),
  avatar: s.string(),
})

const telegramProviderSchema = s.object({
  type: s.literal('telegram'),
  id: s.string(),
  username: s.string().optional(),
  name: s.string(),
  avatar: s.string().optional(),
})

const appleProviderSchema = s.object({
  type: s.literal('apple'),
  id: s.string(),
  email: s.string().optional(),
  name: s.string(),
})

export const providerSchema = s.discriminatedUnion('type', [
  emailProviderSchema,
  googleProviderSchema,
  telegramProviderSchema,
  appleProviderSchema,
])

export type Provider = s.infer<typeof providerSchema>

const providersSchema = s.array(providerSchema)
const settingsSchema = s.record(s.string(), s.unknown())
const userRoleCodes = {
  user: 0,
  admin: 1,
  dev: 2,
  test: 3,
} as const
const userRoles = enumValues(userRoleCodes)

export const users = sqliteTable({
  columns: (c) => ({
    id: c.ulid().notNull().primaryKey(),
    email: c.text().notNull().unique(),
    emailVerified: c.boolean().notNull().default(false),
    name: c.text(),
    avatar: c.text(),
    role: c.enum(userRoleCodes).notNull().default('user'),
    providers: c
      .json(providersSchema)
      .notNull()
      .default([] as Provider[]),
    country: c.text(),
    state: c.text(),
    settings: c.json(settingsSchema).notNull().default({}),
    createdAt: c.datetime().notNull(),
    lastSigninAt: c.datetime(),
  }),
})

export const sessions = sqliteTable({
  columns: (c) => ({
    id: c.ulid().notNull().primaryKey(),
    userId: c
      .ulid()
      .notNull()
      .references(() => users.id),
    ip: c.text(),
    userAgent: c.text(),
    country: c.text(),
    createdAt: c.datetime().notNull(),
    expiresAt: c.datetime().notNull(),
  }),
  indexes: (t) => [index(t.userId), index(t.expiresAt)],
})

interface ResponseSchema<T> {
  parse(data: unknown): T
}

type User = typeof users.__selectType

const responseDateSchema = s.preprocess(
  (value) => value instanceof Date ? value : new Date(typeof value === 'string' ? value : NaN),
  s.date(),
)

export const authUserSchema = s.object({
  id: s.string(),
  email: s.string(),
  emailVerified: s.boolean(),
  name: s.string().nullable(),
  avatar: s.string().nullable(),
  role: s.enum(userRoles),
  providers: s.array(providerSchema),
  country: s.string().nullable(),
  state: s.string().nullable(),
  settings: settingsSchema,
  createdAt: responseDateSchema,
  lastSigninAt: responseDateSchema.nullable(),
}) as ResponseSchema<User>

const userResponseEnvelopeSchema = s.object({
  ok: s.literal(true),
  user: s.unknown(),
})
const sessionResponseEnvelopeSchema = s.object({
  ok: s.boolean(),
  user: s.unknown().nullable(),
})

export const authOkResponseSchema = s.object({ ok: s.literal(true) })
export const authErrorResponseSchema = s.object({ error: s.string() })
export const authOAuthInitResponseSchema = s.object({
  ok: s.literal(true),
  authUrl: s.string(),
})
export const authUserResponseSchema: ResponseSchema<{
  ok: true
  user: User
}> = {
  parse(data) {
    const response = userResponseEnvelopeSchema.parse(data)
    return {
      ok: response.ok,
      user: authUserSchema.parse(response.user),
    }
  },
}
export const authSessionResponseSchema: ResponseSchema<{
  ok: boolean
  user: User | null
}> = {
  parse(data) {
    const response = sessionResponseEnvelopeSchema.parse(data)
    return {
      ok: response.ok,
      user: response.user === null ? null : authUserSchema.parse(response.user),
    }
  },
}

export const refreshTokens = sqliteTable({
  columns: (c) => ({
    id: c.ulid().notNull().primaryKey(),
    // Not unique: a session holds its current token plus, after a rotation, the replaced one during its reuse grace.
    sessionId: c
      .ulid()
      .notNull()
      .references(() => sessions.id),
    tokenHash: c.text().notNull().unique(),
    createdAt: c.datetime().notNull(),
    expiresAt: c.datetime().notNull(),
    /** When a rotation replaced this token; null for the session's current token. */
    replacedAt: c.datetime(),
  }),
  indexes: (t) => [index(t.sessionId), index(t.expiresAt)],
})

export const magicLinks = sqliteTable({
  columns: (c) => ({
    token: c.text().notNull().primaryKey(),
    email: c.text().notNull(),
    createdAt: c.datetime().notNull(),
    expiresAt: c.datetime().notNull(),
  }),
  indexes: (t) => [index(t.email), index(t.expiresAt)],
})

export const oauthStates = sqliteTable({
  columns: (c) => ({
    state: c.text().notNull().primaryKey(),
    provider: c.text().notNull(),
    redirectUri: c.text().notNull(),
    codeVerifier: c.text().notNull(),
    createdAt: c.datetime().notNull(),
    expiresAt: c.datetime().notNull(),
  }),
  indexes: (t) => [index(t.expiresAt)],
})
